import React from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {ProjectIndicator} from '@/web-components';
import {ProjectIndicator_project$data} from './__generated__/ProjectIndicator_project.graphql';
import {ProjectIndicatorWrapper} from './support/DeprecatedProjectIndicator.styled';
import {getProjectLink} from './support/ProjectIndicatorLogic';

type DeprecatedProjectIndicatorComponentProps = {
	companyProjectId: number | null;
	customProjectId: string | null;
	projectName?: string | null;
	projectColor: string | null;
	disableLink?: boolean;
	clearSubPath?: boolean;
	showProjectName?: boolean;
	noWidthLimit?: boolean;
	noWrapper?: boolean;
	small?: boolean;
};

export const DeprecatedProjectIndicatorComponent = ({
	companyProjectId,
	customProjectId,
	projectName,
	projectColor,
	disableLink = false,
	clearSubPath = false,
	showProjectName = false,
	noWidthLimit = true,
	noWrapper = false,
	small = false,
}: DeprecatedProjectIndicatorComponentProps): JSX.Element => {
	const link = getProjectLink(customProjectId, clearSubPath, disableLink);

	// Workaround for nested_dropdown that breaks with the 100% width added by the ProjectIndicatorWrapper
	const WrapperComponent = noWrapper ? React.Fragment : ProjectIndicatorWrapper;

	return (
		<WrapperComponent>
			<ProjectIndicator
				id={companyProjectId}
				customId={customProjectId}
				noWidthLimit={noWidthLimit}
				name={projectName}
				link={link}
				disableLink={disableLink}
				color={projectColor || '#FFFFFF'}
				clearSubPath={clearSubPath}
				isConnectedProject={false}
				small={small}
			/>
			{showProjectName ? <div title={projectName ?? undefined}>{projectName}</div> : null}
		</WrapperComponent>
	);
};

type DeprecatedProjectIndicatorRelayProps = {
	project: ProjectIndicator_project$data;
	disableLink?: boolean;
	clearSubPath?: boolean;
	showProjectName?: boolean;
	noWidthLimit?: boolean;
	noWrapper?: boolean;
	small?: boolean;
};
const DeprecatedProjectIndicator = ({
	project,
	disableLink = false,
	clearSubPath = false,
	showProjectName = false,
	noWidthLimit = true,
	noWrapper = false,
	small = false,
}: DeprecatedProjectIndicatorRelayProps): JSX.Element => {
	return (
		<DeprecatedProjectIndicatorComponent
			companyProjectId={project.companyProjectId}
			customProjectId={project.customProjectId}
			projectName={project.name}
			projectColor={project.projectColor}
			disableLink={disableLink}
			clearSubPath={clearSubPath}
			showProjectName={showProjectName}
			noWidthLimit={noWidthLimit}
			noWrapper={noWrapper}
			small={small}
		/>
	);
};

// Exported raw component to reuse it in DeprecatedProjectIndicatorJS that wraps it in a JS fragment (since JS and TS fragments are incompatible)
export {DeprecatedProjectIndicator as DeprecatedProjectIndicatorNoFragment};

export default createFragmentContainer(DeprecatedProjectIndicator, {
	project: graphql`
		fragment DeprecatedProjectIndicator_project on ProjectType {
			name
			companyProjectId
			customProjectId
			projectColor
		}
	`,
});
